// ============================================================
// Halaman Pesanan — shop orders + SPK per channel
// ============================================================

import axios from '../../config/axios.js';
import Alpine from 'alpinejs';

const STATUS_LABELS = {
  pending: 'Pending',
  diproses: 'Diproses',
  desain: 'Desain',
  printing: 'Printing',
  finishing: 'Finishing',
  dikirim: 'Dikirim',
  selesai: 'Selesai',
  dibatalkan: 'Dibatalkan',
  draft: 'Draft',
  ready: 'Siap',
  production: 'Produksi',
};

const STATUS_BADGES = {
  pending: 'badge-gray',
  diproses: 'badge-yellow',
  desain: 'badge-blue',
  printing: 'badge-blue',
  finishing: 'badge-blue',
  dikirim: 'badge-orange',
  selesai: 'badge-green',
  dibatalkan: 'badge-red',
  draft: 'badge-gray',
  ready: 'badge-blue',
  production: 'badge-yellow',
};

const PAY_LABELS = {
  unpaid: 'Belum Bayar',
  pending: 'Pending',
  pending_confirmation: 'Konfirmasi',
  paid: 'Lunas',
  failed: 'Gagal',
  refunded: 'Refund',
};

const PAY_BADGES = {
  unpaid: 'badge-red',
  pending: 'badge-yellow',
  pending_confirmation: 'badge-orange',
  paid: 'badge-green',
  failed: 'badge-red',
  refunded: 'badge-gray',
};

function isoDate(d) {
  const x = d instanceof Date ? d : new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function createKasirOrders(config = {}) {
  return {
    orders: [],
    loading: true,
    page: 1,
    perPage: 30,
    meta: { total: 0, last_page: 1 },
    summary: {
      total_count: 0,
      total_revenue: 0,
      paid_count: 0,
      unpaid_count: 0,
      by_channel: {},
    },
    activeChannel: 'all',
    activeTab: 'all',
    search: '',
    filterDate: '',
    dateFrom: '',
    dateTo: '',
    datePreset: 'all',
    filterPayment: '',
    today: isoDate(new Date()),
    unreadCount: 0,
    posUrl: config.posUrl || '',
    detailUrl: config.detailUrl || '',

    channelTabs: [
      { value: 'all', label: 'Semua Channel' },
      { value: 'pos', label: 'Harga Jual / POS' },
      { value: 'app', label: 'Aplikasi' },
      { value: 'web', label: 'Web' },
      { value: 'inko', label: 'INKO' },
      { value: 'surang', label: 'SURANG' },
    ],

    statusTabs: [
      { value: 'all', label: 'Semua', count: 0 },
      { value: 'pending', label: 'Pending', count: 0 },
      { value: 'pending_confirmation', label: 'Konfirmasi Bayar', count: 0 },
      { value: 'diproses', label: 'Diproses', count: 0 },
      { value: 'selesai', label: 'Selesai', count: 0 },
    ],

    showDetailModal: false,
    detailOrder: null,
    detailLoading: false,

    showConfirmModal: false,
    confirmTarget: null,
    confirmNote: '',
    confirmLoading: false,
    _blockOutside: false,

    showNotifyModal: false,
    notifyTarget: null,
    notifyTitle: '',
    notifyBody: '',
    notifyPush: true,
    notifyLoading: false,

    async init() {
      // Default: semua tanggal (bukan hanya hari ini) agar data lengkap tampil.
      this.datePreset = 'all';
      this.filterDate = '';
      this.dateFrom = '';
      this.dateTo = '';
      await this.fetchOrders();
      await this.fetchTabCounts();

      setInterval(() => {
        this.fetchOrders(true);
        this.fetchTabCounts();
      }, 30000);

      if (window.Echo) {
        window.Echo.private('admin.dashboard').listen('.order.new', (e) => {
          this.unreadCount++;
          window.toast('Order baru masuk: ' + e.order_number, 'info', 5000);
          this.fetchOrders(true);
        });
      }
    },

    applyDatePreset(preset) {
      this.datePreset = preset;
      this.filterDate = '';
      const today = new Date();
      if (preset === 'today') {
        this.filterDate = isoDate(today);
        this.dateFrom = '';
        this.dateTo = '';
      } else if (preset === '7d') {
        const from = new Date(today);
        from.setDate(from.getDate() - 6);
        this.dateFrom = isoDate(from);
        this.dateTo = isoDate(today);
      } else if (preset === '30d') {
        const from = new Date(today);
        from.setDate(from.getDate() - 29);
        this.dateFrom = isoDate(from);
        this.dateTo = isoDate(today);
      } else {
        this.dateFrom = '';
        this.dateTo = '';
      }
      this.page = 1;
      this.fetchOrders();
      this.fetchTabCounts();
    },

    onDateChange() {
      this.datePreset = this.filterDate ? 'custom' : 'all';
      this.dateFrom = '';
      this.dateTo = '';
      this.page = 1;
      this.fetchOrders();
      this.fetchTabCounts();
    },

    clearDate() {
      this.applyDatePreset('all');
    },

    buildBaseParams() {
      const params = new URLSearchParams({
        page: this.page,
        per_page: this.perPage,
      });
      if (this.search) params.set('search', this.search);
      if (this.filterDate) {
        params.set('date', this.filterDate);
      } else {
        if (this.dateFrom) params.set('date_from', this.dateFrom);
        if (this.dateTo) params.set('date_to', this.dateTo);
      }
      if (this.filterPayment) params.set('payment_status', this.filterPayment);
      if (this.activeChannel && this.activeChannel !== 'all') {
        params.set('pricing_channel', this.activeChannel);
      }
      return params;
    },

    async fetchOrders(silent = false) {
      if (!silent) this.loading = true;
      try {
        const params = this.buildBaseParams();
        if (this.activeTab === 'pending_confirmation') {
          params.set('payment_status', 'pending_confirmation');
        } else if (this.activeTab !== 'all') {
          params.set('status', this.activeTab);
        }
        const { data } = await axios.get('/api/v1/orders?' + params);
        this.orders = data.data || [];
        this.meta = data.meta || {};
        this.summary = data.summary || this.summary;
        this.unreadCount = 0;
      } catch {
        if (!silent) window.toast('Gagal memuat order.', 'error');
      } finally {
        if (!silent) this.loading = false;
      }
    },

    async fetchTabCounts() {
      try {
        const counts = await Promise.all(
          this.statusTabs.slice(1).map((tab) => {
            const p = this.buildBaseParams();
            p.set('page', '1');
            p.set('per_page', '1');
            p.delete('payment_status');
            if (tab.value === 'pending_confirmation') {
              p.set('payment_status', 'pending_confirmation');
            } else {
              p.set('status', tab.value);
            }
            return axios
              .get('/api/v1/orders?' + p)
              .then((r) => ({ value: tab.value, count: r.data.meta?.total || 0 }))
              .catch(() => ({ value: tab.value, count: 0 }));
          }),
        );
        counts.forEach((c) => {
          const tab = this.statusTabs.find((t) => t.value === c.value);
          if (tab) tab.count = c.count;
        });
      } catch {
        /* ignore */
      }
    },

    setChannel(value) {
      this.activeChannel = value;
      this.page = 1;
      this.fetchOrders();
      this.fetchTabCounts();
    },

    setStatusTab(value) {
      this.activeTab = value;
      this.page = 1;
      this.fetchOrders();
    },

    onSearch() {
      this.page = 1;
      this.fetchOrders();
      this.fetchTabCounts();
    },

    onPaymentFilter() {
      this.page = 1;
      this.fetchOrders();
      this.fetchTabCounts();
    },

    async openDetail(order) {
      if (order.record_type === 'spk') {
        if (order.spk_preview_url) window.open(order.spk_preview_url, '_blank');
        return;
      }
      const id = order.id || order.order_number;
      if (!id) {
        window.toast('ID pesanan tidak tersedia.', 'error');
        return;
      }
      if (this.detailUrl) {
        window.location.href = this.detailUrl.replace('__ID__', encodeURIComponent(id));
        return;
      }
      const base = window.location.pathname.includes('/sys/admin') ? '/sys/admin' : '/ops/k9x';
      window.location.href = base + '/orders/' + encodeURIComponent(id);
    },

    closeDetail() {
      this.showDetailModal = false;
      this.detailOrder = null;
    },

    openConfirmModal(order) {
      this._blockOutside = true;
      this.confirmTarget = order;
      this.confirmNote = '';
      this.showConfirmModal = true;
      this.$nextTick(() => {
        setTimeout(() => { this._blockOutside = false; }, 80);
      });
    },

    openNotifyModal(order) {
      this._blockOutside = true;
      this.notifyTarget = order;
      this.notifyTitle = 'Update pesanan ' + (order.order_number || '');
      this.notifyBody = '';
      this.notifyPush = true;
      this.showNotifyModal = true;
      this.$nextTick(() => {
        setTimeout(() => { this._blockOutside = false; }, 80);
      });
    },

    closeNotifyModal() {
      if (this._blockOutside) return;
      this.showNotifyModal = false;
      this.notifyTarget = null;
    },

    async submitNotify() {
      if (!this.notifyTarget?.id) return;
      if (!this.notifyTitle.trim() || !this.notifyBody.trim()) {
        window.toast('Judul dan pesan wajib diisi.', 'warning');
        return;
      }
      this.notifyLoading = true;
      try {
        await axios.post('/api/v1/orders/' + this.notifyTarget.id + '/notify', {
          title: this.notifyTitle.trim(),
          body: this.notifyBody.trim(),
          send_push: !!this.notifyPush,
        });
        window.toast('Notifikasi dikirim ke customer.', 'success');
        this._blockOutside = false;
        this.showNotifyModal = false;
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal mengirim notifikasi.', 'error');
      } finally {
        this.notifyLoading = false;
      }
    },

    async submitConfirm() {
      this.confirmLoading = true;
      try {
        await axios.post('/api/v1/orders/' + this.confirmTarget.id + '/confirm-payment', {
          note: this.confirmNote,
        });
        window.toast(
          'Pembayaran order ' + this.confirmTarget.order_number + ' dikonfirmasi!',
          'success',
        );
        this.showConfirmModal = false;
        this.fetchOrders();
        this.fetchTabCounts();
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal konfirmasi.', 'error');
      } finally {
        this.confirmLoading = false;
      }
    },

    async updateStatus(order, newStatus) {
      if (!newStatus) return;
      try {
        await axios.post('/api/v1/orders/' + order.id + '/status', { status: newStatus });
        window.toast('Status order diperbarui: ' + this.statusLabel(newStatus), 'success');
        const found = this.orders.find((o) => o.id === order.id);
        if (found) found.status = newStatus;
        this.fetchTabCounts();
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal update status.', 'error');
      }
    },

    processToPos(order) {
      if (!this.posUrl) return;
      window.location.href = this.posUrl + '?source=online&order_id=' + order.id;
    },

    downloadInvoice(order) {
      window.open('/invoices/' + order.order_number + '/download', '_blank');
    },

    printSpk(order) {
      if (order.spk_print_url) {
        window.open(order.spk_print_url, '_blank');
      } else {
        window.toast('SPK belum tersedia untuk order ini.', 'warning');
      }
    },

    printSpkInvoice(order) {
      if (order.spk_invoice_print_url) {
        window.open(order.spk_invoice_print_url, '_blank');
      } else {
        window.toast('Invoice SPK belum tersedia.', 'warning');
      }
    },

    activeChannelLabel() {
      const t = this.channelTabs.find((x) => x.value === this.activeChannel);
      return t ? t.label : 'Semua';
    },

    dateFilterLabel() {
      if (this.filterDate) return this.filterDate;
      if (this.dateFrom || this.dateTo) {
        return (this.dateFrom || '…') + ' → ' + (this.dateTo || '…');
      }
      return 'Semua tanggal';
    },

    channelCount(tabValue) {
      if (tabValue === 'all') return this.summary.total_count || 0;
      return this.summary.by_channel?.[tabValue]?.count || 0;
    },

    customerName(order) {
      return order?.user?.name || order?.customer_name || '-';
    },

    customerPhone(order) {
      return order?.user?.phone || order?.customer_phone || '-';
    },

    shippingText(order) {
      const addr = order?.shipping_address;
      if (!addr) return order?.shipping_method || '-';
      if (typeof addr === 'string') return addr;
      const parts = [addr.address, addr.city, addr.province, addr.postal_code].filter(Boolean);
      return parts.length ? parts.join(', ') : (order?.shipping_method || '-');
    },

    orderShortId(id) {
      return '#' + String(id ?? '').slice(0, 8);
    },

    isNew(d) {
      return d && new Date() - new Date(d) < 3600000;
    },

    statusLabel(s) {
      return STATUS_LABELS[s] || s || '—';
    },

    statusBadge(s) {
      return STATUS_BADGES[s] || 'badge-gray';
    },

    payLabel(s) {
      return PAY_LABELS[s] || s || '—';
    },

    payBadge(s) {
      return PAY_BADGES[s] || 'badge-gray';
    },

    fmtRP(n) {
      return Number(n || 0).toLocaleString('id-ID');
    },

    fmtDate(d) {
      return d
        ? new Date(d).toLocaleDateString('id-ID', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : '-';
    },

    timeAgo(d) {
      return typeof window.timeAgo === 'function' ? window.timeAgo(d) : d;
    },
  };
}

Alpine.data('kasirOrders', (config = {}) => createKasirOrders(config));
